import React, { useState, useEffect } from 'react'
import { Link } from 'react-router'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { api } from '../utils/api'
import MeetupCard from '../components/MeetupCard'
import AuthFlow from '../components/AuthFlow'
import Footer from '../components/Footer'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'

export default function Home() {
  const { user } = useAuth()
  const [upcomingMeetups, setUpcomingMeetups] = useState([])
  const [pastMeetups, setPastMeetups] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAuthFlow, setShowAuthFlow] = useState(false)

  useEffect(() => {
    Promise.all([
      api.get('/meetups/upcoming').catch(() => ({ data: { meetups: [] } })),
      api.get('/meetups/past?limit=5').catch(() => ({ data: { meetups: [] } })),
    ]).then(([upcomingRes, pastRes]) => {
      setUpcomingMeetups(upcomingRes.data.meetups)
      setPastMeetups(pastRes.data.meetups)
    }).catch(() => {
      toast.error('Kon meetups niet laden')
    }).finally(() => {
      setLoading(false)
    })
  }, [])

  function handleAuthSuccess() {
    setShowAuthFlow(false)
    window.location.reload()
  }

  return (
    <div className="min-h-screen">
      <section id="meetups" className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          {(upcomingMeetups.length > 0 || loading) && (
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Komende meetups
              </h2>
              {user ? (
                <Link
                  to="/dashboard"
                  className="inline-block bg-primary-600 text-white px-8 py-3 rounded-md text-lg font-semibold hover:bg-primary-700 transition-colors"
                >
                  Mijn dashboard
                </Link>
              ) : (
                <>
                  <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                    Meld je aan om automatisch uitnodigingen per e-mail te krijgen
                  </p>
                  <button
                    onClick={() => setShowAuthFlow(true)}
                    className="bg-primary-600 text-white px-8 py-3 rounded-md text-lg font-semibold hover:bg-primary-700 transition-colors"
                  >
                    Inloggen / Aanmelden
                  </button>
                </>
              )}
            </div>
          )}

          {/* Komende meetups */}
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              <p className="mt-2 text-gray-600 dark:text-gray-400">Meetups laden...</p>
            </div>
          ) : upcomingMeetups.length > 0 ? (
            <div className="flex justify-center">
              <div className={`${upcomingMeetups.length < 3 ? 'flex flex-wrap justify-center gap-6' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'}`}>
                {upcomingMeetups.map((meetup) => (
                  <MeetupCard key={meetup.id} meetup={meetup} />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                Nog geen komende meetups gepland.
              </p>
              {!user && (
                <>
                  <p className="text-gray-500 dark:text-gray-400 mt-2">
                    Schrijf je in voor updates om als eerste op de hoogte te zijn!
                  </p>
                  <button
                    onClick={() => setShowAuthFlow(true)}
                    className="mt-4 bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Inloggen / Aanmelden
                  </button>
                </>
              )}
            </div>
          )}

          {/* Eerdere meetups */}
          {!loading && pastMeetups.length > 0 && (
            <div className="mt-16">
              <div className="border-t border-gray-200 dark:border-gray-700 pt-12">
                <h3 className="text-xl font-semibold text-gray-500 dark:text-gray-400 mb-6 text-center">
                  Eerdere meetups
                </h3>
                <div className="flex justify-center">
                  <div className={`${pastMeetups.length < 3 ? 'flex flex-wrap justify-center gap-6' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'}`}>
                    {pastMeetups.map((meetup) => (
                      <MeetupCard key={meetup.id} meetup={meetup} isPast />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />

      {/* AuthFlow modal */}
      {showAuthFlow && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"
              onClick={() => setShowAuthFlow(false)}
            />
            <div className="relative inline-block bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl sm:my-8 sm:max-w-lg sm:w-full sm:p-6">
              <button
                onClick={() => setShowAuthFlow(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
              <AuthFlow onSuccess={handleAuthSuccess} onClose={() => setShowAuthFlow(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
